import { useRef, useState, type ChangeEvent, type FC } from 'react'

import { Box, SearchField, Text } from '@factorialco/deprecated-design-system'
import {
  TeamChartSearchBarDocument,
  TeamChartSearchBarResultMemberFragment,
} from 'generated/resources/graphql'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { usePeopleChartContext } from 'modules/people_chart/hooks/usePeopleChartContext'

import SearchBarItem, { SearchItem } from './SearchBarItem'

interface MembersResult {
  searchResults: SearchItem[]
  employeeTeams: Record<string, string[]>
}

type Props = {
  disabled?: boolean
  onSelection: (item: SearchItem, teamIds: string[]) => void
}

const SearchBar: FC<Props> = ({ disabled = false, onSelection }) => {
  const { hierarchyTeamIds } = usePeopleChartContext()
  const [fetchResults, { data: results, loading: loading, networkActivity }] = useLazyQuery(
    TeamChartSearchBarDocument,
    { variables: { teamIds: hierarchyTeamIds } }
  )
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [openResult, setOpenResult] = useState(false)
  const resultBoxRef = useRef<HTMLDivElement>(null)

  const handleSelection = (item: SearchItem) => {
    const teamIds =
      item.type === 'employee' ? membersResults.employeeTeams[item.id] || [] : [String(item.id)]
    onSelection(item, teamIds)
    setOpenResult(false)
  }

  const handleOnSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onSearch(event)
    setOpenResult(true)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (resultBoxRef.current && !resultBoxRef.current.contains(event.target as Node)) {
      setOpenResult(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useUnsafeEffect(() => {
    if (debouncedSearch.length && openResult && hierarchyTeamIds.length > 0) {
      fetchResults({
        variables: {
          teamIds: hierarchyTeamIds,
          searchTerm: debouncedSearch,
        },
      })
    }
  }, [debouncedSearch, openResult, hierarchyTeamIds, fetchResults])

  const emptyMembersResult: MembersResult = { searchResults: [], employeeTeams: {} }

  const membersResults: MembersResult = (results?.teams?.membershipsConnection.nodes ?? []).reduce(
    (acc: MembersResult, member: TeamChartSearchBarResultMemberFragment) => {
      const employeeId = member.employee.id
      if (!acc.employeeTeams[employeeId]) {
        acc.employeeTeams[employeeId] = []

        acc.searchResults.push({
          id: member.employee.id,
          name: member.employee.fullName,
          description: member.employee.referenceContract?.jobTitle || '',
          avatar: member.employee.access?.avatar || '',
          type: 'employee',
        })
      }

      acc.employeeTeams[employeeId]?.push(String(member.team?.id || ''))

      return acc
    },
    emptyMembersResult
  )

  const teamResults: SearchItem[] =
    results?.teams?.teamsConnection.edges.map((edge) => ({
      id: edge.node.id,
      name: edge.node.name,
      description: i18n.tp('people_chart.search_bar.team_memberships', {
        count: edge.node.members.totalCount || 0,
      }),
      avatar: edge.node.avatar || '',
      type: 'team',
    })) || []

  const searchResults = [...membersResults.searchResults, ...teamResults]

  return (
    <Box flexGrow>
      <SearchField
        loading={loading && networkActivity}
        search={search}
        disabled={disabled}
        onSearch={handleOnSearch}
        placeholder={i18n.t('people_chart.action_bar.placeholder')}
      />
      {openResult && (
        <Box
          ref={resultBoxRef}
          aria-label={i18n.t('people_chart.search_results')}
          border={{
            all: {
              color: 'grey400',
              width: 's2',
              style: 'solid',
            },
          }}
          borderRadius={{
            all: 'abs010',
          }}
          style={{
            position: 'absolute',
            background: 'white',
            top: '16%',
            left: '2%',
            width: '1020px',
          }}
          gap="s12"
          maxHeight="s300"
          overflowY="auto"
          boxShadow="s50"
        >
          <Box paddingY="s16" paddingX="s16">
            <Text.Normal weight="semibold" color="grey800" size="200">
              {loading && networkActivity
                ? i18n.t('people_chart.search_bar.loading_results')
                : i18n.tp('people_chart.search_bar.total_results', { count: searchResults.length })}
            </Text.Normal>
          </Box>

          {searchResults.map((item) => {
            return (
              <SearchBarItem
                onClick={handleSelection}
                key={`${item.type}-${item.id}`}
                item={item}
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default SearchBar
